import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { RiShoppingCartLine, RiStore2Line, RiHistoryLine, RiBankLine } from "react-icons/ri";
import { MdDelete, MdCancel } from "react-icons/md";
import "./Marketplace.css";

const Marketplace = () => {
    const [merchants, setMerchants] = useState([]);
    const [selectedMerchant, setSelectedMerchant] = useState(null);
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState([]);
    const [banks, setBanks] = useState([]);
    const [orders, setOrders] = useState([]);
    const [activeTab, setActiveTab] = useState("shop"); // shop, cart, history
    const [loading, setLoading] = useState(false);
    const [paymentType, setPaymentType] = useState(1); // 1: Outright, 2: Loan
    const [selectedBank, setSelectedBank] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [merchantSearchTerm, setMerchantSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const productsPerPage = 10;

    const marketplaceUrl = process.env.REACT_APP_MARKETPLACE_URL;
    const providerId = sessionStorage.getItem("healthCareProviderId");

    useEffect(() => {
        fetchMerchants();
        fetchOrderHistory();
    }, []);

    useEffect(() => {
        if (selectedMerchant) {
            fetchProducts(selectedMerchant.id);
            setCurrentPage(1); // Reset to first page when merchant changes
        }
    }, [selectedMerchant]);

    // Reset to first page when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const fetchMerchants = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${marketplaceUrl}/onboarding/merchants`);
            setMerchants(res.data);
        } catch (err) {
            toast.error("Failed to fetch merchants");
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async (merchantId) => {
        try {
            setLoading(true);
            const res = await axios.get(`${marketplaceUrl}/onboarding/products?merchantId=${merchantId}`);
            setProducts(res.data);
        } catch (err) {
            toast.error("Failed to fetch products");
        } finally {
            setLoading(false);
        }
    };

    const fetchBanks = async () => {
        try {
            const res = await axios.get(`${marketplaceUrl}/onboarding/banks`);
            setBanks(res.data);
        } catch (err) {
            toast.error("Failed to fetch banks");
        }
    };

    const fetchOrderHistory = async () => {
        if (!providerId) return;
        try {
            const res = await axios.get(`${marketplaceUrl}/wholesale/orders?healthCareProviderId=${providerId}`);
            setOrders(res.data);
        } catch (err) {
            console.error("Failed to fetch order history", err);
        }
    };

    const addToCart = (product) => {
        const existing = cart.find((item) => item.productId === product.id);
        if (existing) {
            setCart(
                cart.map((item) =>
                    item.productId === product.id
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                )
            );
        } else {
            setCart([...cart, { ...product, productId: product.id, quantity: 1 }]);
        }
        toast.success(`${product.name} added to cart`);
    };

    const updateQuantity = (productId, delta) => {
        setCart(
            cart.map((item) => {
                if (item.productId === productId) {
                    const newQty = Math.max(1, item.quantity + delta);
                    return { ...item, quantity: newQty };
                }
                return item;
            })
        );
    };

    const removeFromCart = (productId) => {
        setCart(cart.filter((item) => item.productId !== productId));
    };

    const placeOrder = async () => {
        if (cart.length === 0) {
            toast.error("Cart is empty");
            return;
        }
        if (paymentType === 2 && selectedBank === 0) {
            toast.error("Please select a bank for loan payment");
            return;
        }

        const payload = {
            healthcareProviderId: parseInt(providerId),
            merchantId: selectedMerchant.id,
            paymentType: paymentType,
            bankId: paymentType === 2 ? parseInt(selectedBank) : 0,
            items: cart.map((item) => ({
                productId: item.productId,
                quantity: item.quantity,
            })),
        };

        try {
            setLoading(true);
            await axios.post(`${marketplaceUrl}/wholesale/orders`, payload);
            toast.success("Order placed successfully");
            setCart([]);
            setActiveTab("history");
            fetchOrderHistory();
        } catch (err) {
            toast.error("Failed to place order");
        } finally {
            setLoading(false);
        }
    };

    const cancelOrder = async (orderId) => {
        try {
            await axios.post(`${marketplaceUrl}/wholesale/orders/${orderId}/cancel`);
            toast.success("Order cancelled");
            fetchOrderHistory();
        } catch (err) {
            toast.error("Failed to cancel order");
        }
    };

    const deleteOrder = async (orderId) => {
        try {
            await axios.delete(`${marketplaceUrl}/wholesale/orders/${orderId}`);
            toast.success("Order deleted");
            fetchOrderHistory();
        } catch (err) {
            toast.error("Failed to delete order");
        }
    };

    const calculateTotal = () => {
        return cart.reduce((sum, item) => sum + item.wholesalePrice * item.quantity, 0);
    };

    const filteredProducts = products.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredMerchants = merchants.filter(m =>
        m.name.toLowerCase().includes(merchantSearchTerm.toLowerCase())
    );

    // Pagination calculations
    const indexOfLastProduct = currentPage * productsPerPage;
    const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
    const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
    const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        // Scroll to top of product area
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const CartIcon = () => (
        <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
        >
            <circle cx="9" cy="21" r="1"/>
            <circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
        </svg>
    );

    return (
        <div className="marketplace-container">
            <div className="marketplace-header flex justify-between items-center m-b-20">
                <h2 className="bold-text">Marketplace</h2>
                <div className="marketplace-tabs flex">
                    <button
                        className={`tab-btn ${activeTab === "shop" ? "active" : ""}`}
                        onClick={() => setActiveTab("shop")}
                    >
                        <RiStore2Line className="m-r-5" /> Shop
                    </button>
                    <button
                        className={`tab-btn ${activeTab === "cart" ? "active" : ""}`}
                        onClick={() => setActiveTab("cart")}
                    >
                        <span className="cart-icon-wrapper">
                            <CartIcon />
                        </span>
                        <span className="m-l-5">Cart</span>
                        {cart.length > 0 && (
                            <span className="cart-badge">{cart.length}</span>
                        )}
                    </button>
                    <button
                        className={`tab-btn ${activeTab === "history" ? "active" : ""}`}
                        onClick={() => setActiveTab("history")}
                    >
                        <RiHistoryLine className="m-r-5" /> History
                    </button>
                </div>
            </div>

            {activeTab === "shop" && (
                <div className="shop-layout">
                    <div className="merchant-sidebar">
                        <div className="sidebar-header">
                            <RiStore2Line className="m-r-10" size={20} />
                            <h3>Merchants</h3>
                        </div>
                        <div className="merchant-search m-b-15">
                            <input
                                type="text"
                                placeholder="Search merchants..."
                                value={merchantSearchTerm}
                                onChange={(e) => setMerchantSearchTerm(e.target.value)}
                                className="merchant-search-input"
                            />
                        </div>
                        {loading && merchants.length === 0 ? (
                            <div className="loading-shimmer-list">
                                {[1, 2, 3].map(i => <div key={i} className="shimmer-item"></div>)}
                            </div>
                        ) : (
                            <ul className="merchant-list scrollable-list">
                                {filteredMerchants.map((m) => (
                                    <li
                                        key={m.id}
                                        className={`merchant-item ${selectedMerchant?.id === m.id ? "selected" : ""}`}
                                        onClick={() => setSelectedMerchant(m)}
                                    >
                                        <div className="merchant-avatar">
                                            {m.name.charAt(0)}
                                        </div>
                                        <span className="merchant-name">{m.name}</span>
                                    </li>
                                ))}
                                {filteredMerchants.length === 0 && (
                                    <p className="text-muted text-center m-t-10">No merchants found</p>
                                )}
                            </ul>
                        )}
                    </div>
                    <div className="product-area">
                        {selectedMerchant ? (
                            <>
                                <div className="product-area-header flex justify-between items-center m-b-20">
                                    <div className="title-group">
                                        <h3 className="m-b-5">Products from {selectedMerchant.name}</h3>
                                        <p className="text-muted">{filteredProducts.length} items available</p>
                                    </div>
                                    <div className="search-box">
                                        <input
                                            type="text"
                                            placeholder="Search products..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {loading ? (
                                    <div className="product-grid">
                                        {[1, 2, 3, 4].map(i => <div key={i} className="product-card shimmer"></div>)}
                                    </div>
                                ) : filteredProducts.length > 0 ? (
                                    <>
                                        <div className="product-grid">
                                            {currentProducts.map((p) => (
                                                <div key={p.id} className="product-card">
                                                    <div className="product-image-placeholder">
                                                        {p.uploadPaths && p.uploadPaths.length > 0 ? (
                                                            <img
                                                                src={p.uploadPaths[0]}
                                                                alt={p.name}
                                                                className="product-image"
                                                                onError={(e) => {
                                                                    e.target.style.display = "none";
                                                                    e.target.nextSibling.style.display = "flex";
                                                                }}
                                                            />
                                                        ) : null}
                                                        <div
                                                            className="product-image-fallback"
                                                            style={{
                                                                display: p.uploadPaths && p.uploadPaths.length > 0 ? "none" : "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                width: "100%",
                                                                height: "100%",
                                                            }}
                                                        >
                                                            <RiStore2Line size={40} color="#e2e8f0" />
                                                        </div>
                                                    </div>
                                                    <div className="product-info">
                                                        <h4 className="product-name">{p.name}</h4>
                                                        <div className="product-meta flex justify-between items-center">
                                                            <p className="price">₦{p.wholesalePrice.toLocaleString()}</p>
                                                            <p className="stock">In Stock: {p.stockQuantity}</p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        className="add-to-cart-btn-merchant"
                                                        onClick={() => addToCart(p)}
                                                        disabled={p.stockQuantity <= 0}
                                                    >
                                                        {p.stockQuantity > 0 ? (
                                                            <>
                                                                <span className="cart-icon-wrapper">
                                                                    Add to cart&nbsp;
                                                                    <CartIcon />
                                                                </span>
                                                            </>
                                                        ) : "Out of Stock"}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Pagination Controls */}
                                        {totalPages > 1 && (
                                            <div className="pagination-container">
                                                <button 
                                                    className="pagination-btn"
                                                    onClick={() => paginate(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                >
                                                    Previous
                                                </button>
                                                
                                                <div className="pagination-numbers">
                                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => {
                                                        // Show first page, last page, current page, and pages around current
                                                        if (
                                                            number === 1 ||
                                                            number === totalPages ||
                                                            (number >= currentPage - 1 && number <= currentPage + 1)
                                                        ) {
                                                            return (
                                                                <button
                                                                    key={number}
                                                                    onClick={() => paginate(number)}
                                                                    className={`pagination-number ${currentPage === number ? 'active' : ''}`}
                                                                >
                                                                    {number}
                                                                </button>
                                                            );
                                                        } else if (
                                                            number === currentPage - 2 ||
                                                            number === currentPage + 2
                                                        ) {
                                                            return <span key={number} className="pagination-ellipsis">...</span>;
                                                        }
                                                        return null;
                                                    })}
                                                </div>

                                                <button 
                                                    className="pagination-btn"
                                                    onClick={() => paginate(currentPage + 1)}
                                                    disabled={currentPage === totalPages}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="no-results">
                                        <p>No products match your search.</p>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="select-merchant-prompt">
                                <div className="prompt-icon-wrapper">
                                    <RiStore2Line size={80} color="#3C7E2D" />
                                </div>
                                <h3>Welcome to the Marketplace</h3>
                                <p>Select a merchant from the left to start browsing their wholesale catalog.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === "cart" && (
                <div className="cart-container">
                    <div className="cart-header m-b-20">
                        <h3>Your Shopping Cart</h3>
                        <p className="text-muted">Review your items and proceed to checkout</p>
                    </div>
                    {cart.length > 0 ? (
                        <div className="cart-content">
                            <div className="table-responsive">
                                <table className="cart-table">
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>Price</th>
                                            <th>Quantity</th>
                                            <th>Subtotal</th>
                                            <th className="text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cart.map((item) => (
                                            <tr key={item.productId}>
                                                <td>
                                                    <div className="cart-item-info">
                                                        <span className="item-name">{item.name}</span>
                                                    </div>
                                                </td>
                                                <td>₦{item.wholesalePrice.toLocaleString()}</td>
                                                <td>
                                                    <div className="qty-controls">
                                                        <button onClick={() => updateQuantity(item.productId, -1)}>-</button>
                                                        <span className="qty-value">{item.quantity}</span>
                                                        <button onClick={() => updateQuantity(item.productId, 1)}>+</button>
                                                    </div>
                                                </td>
                                                <td className="subtotal">₦{(item.wholesalePrice * item.quantity).toLocaleString()}</td>
                                                <td className="text-center">
                                                    <button className="remove-btn-icon" onClick={() => removeFromCart(item.productId)}>
                                                        <MdDelete size={22} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="cart-summary-layout m-t-30">
                                <div className="checkout-section">
                                    <div className="payment-options m-b-24">
                                        <h4 className="m-b-15">Payment Method</h4>
                                        <div className="payment-grid">
                                            <label className={`payment-card ${paymentType === 1 ? 'selected' : ''}`}>
                                                <input
                                                    type="radio"
                                                    name="paymentType"
                                                    value={1}
                                                    checked={paymentType === 1}
                                                    onChange={() => setPaymentType(1)}
                                                />
                                                <div className="payment-card-content">
                                                    <span className="payment-title">Outright Payment</span>
                                                    <span className="payment-desc">Pay immediately for your order</span>
                                                </div>
                                            </label>
                                            <label className={`payment-card ${paymentType === 2 ? 'selected' : ''}`}>
                                                <input
                                                    type="radio"
                                                    name="paymentType"
                                                    value={2}
                                                    checked={paymentType === 2}
                                                    onChange={() => {
                                                        setPaymentType(2);
                                                        fetchBanks();
                                                    }}
                                                />
                                                <div className="payment-card-content">
                                                    <span className="payment-title">Loan / Credit</span>
                                                    <span className="payment-desc">Apply for a loan through a bank</span>
                                                </div>
                                            </label>
                                        </div>
                                    </div>

                                    {paymentType === 2 && (
                                        <div className="bank-selection m-b-24">
                                            <h4 className="m-b-10">Select Bank</h4>
                                            <select
                                                value={selectedBank}
                                                onChange={(e) => setSelectedBank(e.target.value)}
                                                className="bank-select-premium"
                                            >
                                                <option value={0}>-- Choose a bank --</option>
                                                {banks.map((b) => (
                                                    <option key={b.id} value={b.id}>
                                                        {b.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>

                                <div className="order-total-card">
                                    <div className="total-row">
                                        <span>Subtotal</span>
                                        <span>₦{calculateTotal().toLocaleString()}</span>
                                    </div>
                                    <div className="total-row">
                                        <span>Tax (0%)</span>
                                        <span>₦0</span>
                                    </div>
                                    <div className="total-row grand-total">
                                        <span>Grand Total</span>
                                        <span>₦{calculateTotal().toLocaleString()}</span>
                                    </div>
                                    <button className="place-order-btn-premium" onClick={placeOrder} disabled={loading}>
                                        {loading ? "Processing..." : "Confirm & Place Order"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="empty-cart-premium">
                            <div className="empty-icon-wrapper">
                                <span className="cart-icon-wrapper-large">
                                    <CartIcon />
                                </span>
                            </div>
                            <h3>Your cart is empty</h3>
                            <p>Looks like you haven't added any wholesale products yet.</p>
                            <button className="btn-primary-premium m-t-20" onClick={() => setActiveTab("shop")}>
                                Start Shopping
                            </button>
                        </div>
                    )}
                </div>
            )}

            {activeTab === "history" && (
                <div className="history-container">
                    <div className="history-header m-b-24 flex justify-between items-center">
                        <div>
                            <h3>Order History</h3>
                            <p className="text-muted">Track and manage your wholesale orders</p>
                        </div>
                        <button className="btn-refresh" onClick={fetchOrderHistory}>
                            <RiHistoryLine className="m-r-5" /> Refresh
                        </button>
                    </div>
                    {orders.length > 0 ? (
                        <div className="orders-grid">
                            {orders.map((order) => {
                                const merchant = merchants.find(m => m.id === order.merchantId);
                                const merchantName = merchant ? merchant.name : `Merchant #${order.merchantId}`;
                                const totalAmount = order.items?.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0) || 0;

                                return (
                                    <div key={order.id} className="order-card-premium">
                                        <div className="order-card-header flex justify-between items-start">
                                            <div className="order-id-group">
                                                <span className="order-label">Order Reference</span>
                                                <h4 className="order-id">#WH-{order.id.toString().padStart(5, '0')}</h4>
                                                <span className="order-date">{new Date(order.orderDate).toLocaleDateString(undefined, { dateStyle: 'medium' })}</span>
                                            </div>
                                            <span className={`order-status-badge status-${order.status}`}>
                                                {getStatusLabel(order.status)}
                                            </span>
                                        </div>
                                        <div className="order-card-body m-t-20">
                                            <div className="info-row">
                                                <span className="label">Merchant</span>
                                                <span className="value bold-text">{merchantName}</span>
                                            </div>
                                            <div className="info-row">
                                                <span className="label">Total Amount</span>
                                                <span className="value price-text">₦{totalAmount.toLocaleString()}</span>
                                            </div>
                                        </div>
                                        {order.status === 0 && (
                                            <div className="order-card-footer m-t-20 flex gap-12">
                                                <button className="btn-cancel-premium" onClick={() => cancelOrder(order.id)}>
                                                    <MdCancel className="m-r-5" /> Cancel Order
                                                </button>
                                                <button className="btn-delete-premium" onClick={() => deleteOrder(order.id)}>
                                                    <MdDelete className="m-r-5" /> Delete
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="empty-history">
                            <RiHistoryLine size={80} color="#e2e8f0" />
                            <p>No orders found in your history.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const getStatusLabel = (status) => {
    const statuses = ["Pending", "Paid", "Awaiting Loan Approval", "Completed", "Delivered", "Cancelled"];
    return statuses[status] || "Unknown";
};

export default Marketplace;